import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import Sidebar from '../components/ui/Sidebar';
import Header from '../components/ui/Header';
import { useSidebar } from '../contexts/SidebarContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';

const ThankYou = () => {
  const { user, loading: authLoading } = useAuth();
  const { isPaid, status, loading: subscriptionLoading, billingCycle, nextBillingDate, subscriptionEndDate, refresh } = useSubscription();
  const { sidebarExpanded } = useSidebar();
  const navigate = useNavigate();

  const planLabel = billingCycle === 'annual' ? 'Annual' : billingCycle === 'monthly' ? 'Monthly' : 'Subscription';
  const renewalDateRaw = nextBillingDate || subscriptionEndDate;
  const renewalDateFormatted = renewalDateRaw
    ? new Date(renewalDateRaw).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  // Refetch subscription on mount so we show latest plan (e.g. Annual) after returning from Razorpay
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Redirect non-paid users to upgrade page
  useEffect(() => {
    if (!authLoading && !subscriptionLoading && !isPaid) {
      navigate('/upgrade', { replace: true });
    }
  }, [authLoading, subscriptionLoading, isPaid, navigate]);

  // Show loading while checking subscription status
  if (authLoading || subscriptionLoading || !isPaid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header />
      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center py-12 sm:py-20">
              <div className="text-6xl mb-5 text-success">✅</div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">Welcome to RetirementPro!</h1>
              <h2 className="text-xl sm:text-2xl text-primary font-semibold mb-8">Your subscription is active. Time to gain clarity.</h2>

              <div className="bg-card rounded-2xl border-2 border-success shadow-lg p-6 sm:p-8 mb-10">
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-4">
                  You now have <strong className="font-semibold text-foreground">full, uninterrupted access</strong> to the core planning suite, including Cash-Flow Analysis, Debt Prepayment Modeling, and your live Readiness Score.
                </p>
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="font-semibold text-foreground">Plan: Subscription ({planLabel})</p>
                  {renewalDateFormatted && (
                    <p className="text-sm text-muted-foreground mt-1">Next renewal: {renewalDateFormatted}</p>
                  )}
                </div>
              </div>

              <button
                onClick={() => navigate('/wealth-dashboard')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white rounded-lg font-bold text-lg hover:bg-primary/90 transition-colors"
              >
                Go to Your Dashboard
                <Icon name="ArrowRight" size={20} />
              </button>

              <p className="mt-10 text-sm text-muted-foreground">
                *A receipt has been sent to your registered email address.*
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ThankYou;

